/**
 * Mapping of backend error.code values to user-friendly messages and
 * suggested actions. Used by every clinical write surface so error UX
 * is consistent.
 *
 * Backend convention (see `requireClinicalContext` middleware): errors
 * arrive as `{ error: { code, message, details? } }` on a non-2xx response.
 */

export interface ParsedApiError {
  code: string | null;
  message: string;
  /** Suggested next action the UI can show as a button. */
  action?: { label: string; hint: string };
}

const FRIENDLY: Record<string, ParsedApiError> = {
  NOTE_LOCKED: {
    code: 'NOTE_LOCKED',
    message: 'This note is signed and locked. Create an addendum to add new information.',
    action: { label: 'Create addendum', hint: 'add-addendum' },
  },
  ENCOUNTER_SIGNED: {
    code: 'ENCOUNTER_SIGNED',
    message: 'This visit is signed and closed. Edits require an addendum on the relevant note.',
    action: { label: 'Create addendum', hint: 'add-addendum' },
  },
  MISSING_CLINICAL_CONTEXT: {
    code: 'MISSING_CLINICAL_CONTEXT',
    message: 'A clinical context (encounter or off-encounter reason) is required for this action.',
  },
  INVALID_OFF_ENCOUNTER_REASON: {
    code: 'INVALID_OFF_ENCOUNTER_REASON',
    message: 'The off-encounter reason supplied is not allowed for this surface.',
  },
  AVS_NOT_GENERATED: {
    code: 'AVS_NOT_GENERATED',
    message: 'No after-visit summary has been generated yet for this encounter.',
    action: { label: 'Generate AVS', hint: 'generate-avs' },
  },
  AVS_ALREADY_PUBLISHED: {
    code: 'AVS_ALREADY_PUBLISHED',
    message: 'This after-visit summary has already been published to the patient portal.',
  },
};

export function parseApiError(error: any): ParsedApiError {
  const code: string | undefined = error?.response?.data?.error?.code;
  const fallbackMessage: string =
    error?.response?.data?.error?.message
    || error?.response?.data?.message
    || error?.message
    || 'Something went wrong. Please try again.';

  if (code && FRIENDLY[code]) {
    return { ...FRIENDLY[code], message: FRIENDLY[code].message };
  }
  return { code: code ?? null, message: fallbackMessage };
}
